import React, { useState, useEffect } from 'react';
import { SeriesList } from './admin/components/SeriesList';
import { ColorList } from './admin/components/ColorList';
import { AwardList } from './admin/components/AwardList';
import { ExhibitionList } from './admin/components/ExhibitionList';
import { NewsList } from './admin/components/NewsList';
import { PaintingForm } from './admin/forms/PaintingForm';
import { HomeForm } from './admin/forms/HomeForm';
import { HomePreview } from './admin/forms/HomePreview';
import { TabPanel } from './admin/components/TabPanel';
import { PaintingList } from './admin/components/PaintingList';
import { PaintingFormModal } from './admin/components/PaintingFormModal';
import { SeriesFormModal } from './admin/components/SeriesFormModal';
import { ColorFormModal } from './admin/components/ColorFormModal';
import { AwardFormModal } from './admin/components/AwardFormModal';
import { ExhibitionFormModal } from './admin/components/ExhibitionFormModal';
import { NewsFormModal } from './admin/components/NewsFormModal';
import { AdminProvider, useAdmin } from './admin/context/AdminContext';
import { formStyles } from './admin/utils/formStyles';
import { Squares2X2Icon, SwatchIcon, TrophyIcon, BuildingLibraryIcon, PhotoIcon, HomeIcon } from '@heroicons/react/24/outline';
import { HiOutlineSquares2X2, HiOutlinePhoto, HiOutlineCalendar, HiOutlinePaintBrush, HiOutlineSwatch, HiOutlineTrophy, HiOutlineBuildingLibrary, HiOutlineChartBar } from 'react-icons/hi2';
import useAuthGuard from './admin/utils/useAuthGuard';
import AlertDialog from './common/AlertDialog';
import AdminStats from './admin/components/AdminStats';
import { showSessionExpiryModal } from './admin/utils/sessionExpiryHandler';

const API_URL = import.meta.env.VITE_API_URL;

function AdminContent() {
  const [activeTab, setActiveTab] = useState(0);
  const { loading, error, handleCreate, handleUpdate, handleDelete, fetchData, colors, series, awards, exhibitions, paintings, news } = useAdmin();

  // Handle OAuth2 callback
  useEffect(() => {
    const handleOAuthCallback = () => {
      // Check for OAuth2 tokens in URL fragment
      const fragment = window.location.hash.substring(1);
      if (fragment && (fragment.includes('id_token=') || fragment.includes('access_token='))) {
        console.log('Processing OAuth2 callback...');
        
        const params = new URLSearchParams(fragment);
        const idToken = params.get('id_token');
        const accessToken = params.get('access_token');
        const expiresIn = params.get('expires_in');
        const errorParam = params.get('error');

        if (errorParam) {
          console.error('OAuth2 error:', errorParam);
          if (errorParam === 'insufficient_permissions') {
            alert('Sie haben nicht die erforderlichen Berechtigungen für den Admin-Bereich.');
          }
          // Redirect to login
          window.location.href = '/login';
          return;
        }

        if (idToken && accessToken) {
          // Calculate expiry time
          const expiryTime = expiresIn 
            ? Date.now() + (parseInt(expiresIn) * 1000)
            : Date.now() + (60 * 60 * 1000); // Default 1 hour

          // Store tokens in localStorage
          localStorage.setItem('peart_auth_token', accessToken);
          localStorage.setItem('peart_auth_token_expiry', expiryTime.toString());
          localStorage.setItem('oidc_id_token', idToken);

          console.log('OAuth2 tokens stored successfully');

          // Clear URL fragment
          window.history.replaceState({}, document.title, '/admin');
          
          // Reload to ensure auth state is updated
          window.location.reload();
        }
      }
      
      // Legacy OIDC callback handling
      if (window.location.search.includes('code=')) {
        console.log('Processing legacy OIDC callback...');
      }
    };

    handleOAuthCallback();
  }, []);
  
  // Check token validity on tab focus and periodically
  useEffect(() => {
    const checkTokenOnFocus = () => {
      const expiry = localStorage.getItem('peart_auth_token_expiry');
      const now = Date.now();
      
      if (!expiry || now > parseInt(expiry)) {
        // Token abgelaufen - sofort zur Login-Seite
        console.log('Token expired, redirecting to login');
        localStorage.removeItem('peart_auth_token');
        localStorage.removeItem('peart_auth_token_expiry');
        window.location.href = '/login';
      } else {
        // Check if token expires soon (within 10 minutes)
        const timeUntilExpiry = parseInt(expiry) - now;
        const isOIDC = localStorage.getItem('oidc_id_token') !== null;
        
        // Skip warning for OIDC tokens (they are short-lived by design)
        if (!isOIDC && timeUntilExpiry < 10 * 60 * 1000 && timeUntilExpiry > 0) {
          // Show session expiry modal only for password-based auth
          showSessionExpiryModal({
            onExtend: async () => {
              try {
                // Bei OIDC-Login können wir nicht automatisch verlängern
                // User muss sich neu anmelden
                alert('Ihre Sitzung läuft bald ab. Bitte speichern Sie Ihre Arbeit und melden Sie sich erneut an.');
                window.location.href = '/login';
              } catch (error) {
                console.error('Failed to extend session:', error);
                window.location.href = '/login';
              }
            },
            onCancel: () => {
              // User cancelled - just close modal
              console.log('User cancelled session extension');
            }
          });
        }
      }
    };

    // Check on initial load
    checkTokenOnFocus();
    
    // Check when tab gets focus
    window.addEventListener('focus', checkTokenOnFocus);
    
    // Also check periodically (every 30 seconds) while tab is active
    const interval = setInterval(checkTokenOnFocus, 30000);
    
    return () => {
      window.removeEventListener('focus', checkTokenOnFocus);
      clearInterval(interval);
    };
  }, []);
  
  // Separate form data for each form type
  const [homeFormData, setHomeFormData] = useState({
    title_de: '',
    title_en: '',
    description_de: '',
    description_en: '',
    nav_subtitle_de: '',
    nav_subtitle_en: '',
    hero_subtitle_de: '',
    hero_subtitle_en: '',
    about_description2_de: '',
    about_description2_en: '',
    hero_image: null,
    about_image: null
  });

  // Local form data states (not entity lists - those come from AdminContext)
  const [awardFormData, setAwardFormData] = useState({ name: '', date: '', location: '' });
  const [editAwardId, setEditAwardId] = useState(null);
  const [deleteAwardDialogOpen, setDeleteAwardDialogOpen] = useState(false);
  const [awardToDelete, setAwardToDelete] = useState(null);

  const [seriesFormData, setSeriesFormData] = useState({ name: '' });
  const [editSeriesId, setEditSeriesId] = useState(null);

  const [colorFormData, setColorFormData] = useState({ name_de: '', name_en: '', color_code: '#000000' });
  const [editColorId, setEditColorId] = useState(null);
  const [deleteColorDialogOpen, setDeleteColorDialogOpen] = useState(false);
  const [colorToDelete, setColorToDelete] = useState(null);

  const [exhibitionFormData, setExhibitionFormData] = useState({ name: '', locations: '', start_date: '', end_date: '' });
  const [editExhibitionId, setEditExhibitionId] = useState(null);
  const [deleteExhibitionDialogOpen, setDeleteExhibitionDialogOpen] = useState(false);
  const [exhibitionToDelete, setExhibitionToDelete] = useState(null);

  // Modal states
  const [showSeriesModal, setShowSeriesModal] = useState(false);
  const [showColorModal, setShowColorModal] = useState(false);
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [showExhibitionModal, setShowExhibitionModal] = useState(false);
  const [showNewsModal, setShowNewsModal] = useState(false);
  
  // Current editing items for modals
  const [currentSeries, setCurrentSeries] = useState(null);
  const [currentColor, setCurrentColor] = useState(null);
  const [currentAward, setCurrentAward] = useState(null);
  const [currentExhibition, setCurrentExhibition] = useState(null);
  const [currentNews, setCurrentNews] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [seriesToDelete, setSeriesToDelete] = useState(null);

  const [paintingViews, setPaintingViews] = useState({});
  const [colorViews, setColorViews] = useState({});
  const [awardViews, setAwardViews] = useState({});
  const [exhibitionViews, setExhibitionViews] = useState({});
  const [seriesViews, setSeriesViews] = useState({});

  // Separate function to load views
  const loadViews = async () => {
    try {
      const token = localStorage.getItem("peart_auth_token");
      const res = await fetch(`${API_URL}/stats-views`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const stats = await res.json();
        // Mappe Views für alle Entity-Types
        const paintingViewsMap = {};
        const colorViewsMap = {};
        const awardViewsMap = {};
        const exhibitionViewsMap = {};
        const seriesViewsMap = {};
        stats.forEach(s => {
          if (s.entity_type === 'painting') paintingViewsMap[s.entity_id] = s.views;
          if (s.entity_type === 'color') colorViewsMap[s.entity_id] = s.views;
          if (s.entity_type === 'award') awardViewsMap[s.entity_id] = s.views;
          if (s.entity_type === 'exhibition') exhibitionViewsMap[s.entity_id] = s.views;
          if (s.entity_type === 'series') seriesViewsMap[s.entity_id] = s.views;
        });
        setPaintingViews(paintingViewsMap);
        setColorViews(colorViewsMap);
        setAwardViews(awardViewsMap);
        setExhibitionViews(exhibitionViewsMap);
        setSeriesViews(seriesViewsMap);
      } else {
        setPaintingViews({});
        setColorViews({});
        setAwardViews({});
        setExhibitionViews({});
        setSeriesViews({});
      }
    } catch (e) {
      console.error('Error loading views:', e);
      setPaintingViews({});
      setColorViews({});
      setAwardViews({});
      setExhibitionViews({});
      setSeriesViews({});
    }
  };

  // Load data when switching tabs
  useEffect(() => {
    const loadTabData = async () => {
      try {
        // Load views for all relevant tabs
        if ([1, 2, 3, 4, 5].includes(activeTab)) {
          await loadViews();
        }

        // AdminContext handles data loading automatically

        switch (activeTab) {
          case 0: // Home
            const homeData = await fetchData('home');
            if (homeData) {
              setHomeFormData({
                title_de: homeData.about_title_de || '',
                title_en: homeData.about_title_en || '',
                description_de: homeData.about_description1_de || '',
                description_en: homeData.about_description1_en || '',
                nav_subtitle_de: homeData.nav_subtitle_de || '',
                nav_subtitle_en: homeData.nav_subtitle_en || '',
                hero_subtitle_de: homeData.hero_subtitle_de || '',
                hero_subtitle_en: homeData.hero_subtitle_en || '',
                about_description2_de: homeData.about_description2_de || '',
                about_description2_en: homeData.about_description2_en || '',
                hero_image: homeData.hero_image || null,
                about_image: homeData.about_image || null
              });
            }
            break;
          case 1: // Gallery
            await fetchData('series');
            await fetchData('colors');
            await fetchData('awards');
            await fetchData('exhibitions');
            break;
          case 2: // Series
          case 3: // Colors  
          case 4: // Exhibitions
          case 5: // Awards
            // AdminContext handles data loading automatically
            break;
          case 6: // News
            // Add news data loading if needed
            break;
          case 7: // Stats
            // Add stats data loading if needed
            break;
          default:
            break;
        }
      } catch (err) {
        console.error('Error loading tab data:', err);
      }
    };

    loadTabData();
  }, [activeTab, fetchData]);

  const handleHomeChange = (e) => {
    const { name, value, type, checked } = e.target;
    setHomeFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAwardChange = (e) => {
    const { name, value } = e.target;
    setAwardFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSeriesChange = (e) => {
    const { name, value } = e.target;
    setSeriesFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleColorChange = (e) => {
    const { name, value } = e.target;
    setColorFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleExhibitionChange = (e) => {
    const { name, value, type, checked } = e.target;
    setExhibitionFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleHomeImageChange = (e, field) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setHomeFormData(prev => ({
      ...prev,
      [field]: file
    }));
  };

  const handleHomeSubmit = async (e) => {
    e.preventDefault();
    try {
      const mappedData = {
        ...homeFormData,
        about_title_de: homeFormData.title_de,
        about_title_en: homeFormData.title_en,
        about_description1_de: homeFormData.description_de,
        about_description1_en: homeFormData.description_en
      };
      await handleUpdate('home', null, mappedData);
    } catch (err) {
      console.error('Error submitting home form:', err);
    }
  };

  const handleAwardSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editAwardId) {
        await handleUpdate('awards', editAwardId, awardFormData);
        setEditAwardId(null);
      } else {
        await handleCreate('awards', awardFormData);
      }
      await fetchData('awards');
      setAwardFormData({ name: '', date: '', location: '' });
    } catch (err) {
      console.error('Error submitting award form:', err);
    }
  };

  const handleSeriesSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editSeriesId) {
        await handleUpdate('series', editSeriesId, seriesFormData);
        setEditSeriesId(null);
      } else {
        await handleCreate('series', seriesFormData);
      }
      await fetchData('series');
      setSeriesFormData({ name: '' });
    } catch (err) {
      console.error('Error submitting series form:', err);
    }
  };

  const handleColorSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editColorId) {
        await handleUpdate('colors', editColorId, colorFormData);
        setEditColorId(null);
      } else {
        await handleCreate('colors', colorFormData);
      }
      await fetchData('colors');
      setColorFormData({ name_de: '', name_en: '', color_code: '#000000' });
    } catch (err) {
      console.error('Error submitting color form:', err);
    }
  };

  const handleExhibitionSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editExhibitionId) {
        await handleUpdate('exhibitions', editExhibitionId, exhibitionFormData);
        setEditExhibitionId(null);
        } else {
        await handleCreate('exhibitions', exhibitionFormData);
      }
      await fetchData('exhibitions');
      setExhibitionFormData({ name: '', locations: '', start_date: '', end_date: '' });
    } catch (err) {
      console.error('Error submitting exhibition form:', err);
    }
  };

  const handleEditSeries = (serie) => {
    setCurrentSeries(serie);
    setShowSeriesModal(true);
  };

  const handleCreateSeries = () => {
    setCurrentSeries(null);
    setShowSeriesModal(true);
  };

  const handleDeleteSeries = (id) => {
    setSeriesToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteSeries = async () => {
    if (!seriesToDelete) return;
    await handleDelete('series', seriesToDelete);
    setDeleteDialogOpen(false);
    setSeriesToDelete(null);
  };

  const cancelDeleteSeries = () => {
    setDeleteDialogOpen(false);
    setSeriesToDelete(null);
  };

  const handleEditColor = (color) => {
    setCurrentColor(color);
    setShowColorModal(true);
  };

  const handleCreateColor = () => {
    setCurrentColor(null);
    setShowColorModal(true);
  };

  const handleDeleteColor = (id) => {
    setColorToDelete(id);
    setDeleteColorDialogOpen(true);
  };

  const confirmDeleteColor = async () => {
    if (!colorToDelete) return;
    await handleDelete('colors', colorToDelete);
    setDeleteColorDialogOpen(false);
    setColorToDelete(null);
  };

  const cancelDeleteColor = () => {
    setDeleteColorDialogOpen(false);
    setColorToDelete(null);
  };

  const handleEditExhibition = (exhibition) => {
    setCurrentExhibition(exhibition);
    setShowExhibitionModal(true);
  };

  const handleCreateExhibition = () => {
    setCurrentExhibition(null);
    setShowExhibitionModal(true);
  };

  const handleDeleteExhibition = (id) => {
    setExhibitionToDelete(id);
    setDeleteExhibitionDialogOpen(true);
  };

  const confirmDeleteExhibition = async () => {
    if (!exhibitionToDelete) return;
    await handleDelete('exhibitions', exhibitionToDelete);
    setDeleteExhibitionDialogOpen(false);
    setExhibitionToDelete(null);
  };

  const cancelDeleteExhibition = () => {
    setDeleteExhibitionDialogOpen(false);
    setExhibitionToDelete(null);
  };

  const handleEditAward = (award) => {
    setCurrentAward(award);
    setShowAwardModal(true);
  };

  const handleCreateAward = () => {
    setCurrentAward(null);
    setShowAwardModal(true);
  };

  const handleDeleteAward = (id) => {
    setAwardToDelete(id);
    setDeleteAwardDialogOpen(true);
  };

  const confirmDeleteAward = async () => {
    if (!awardToDelete) return;
    await handleDelete('awards', awardToDelete);
    setDeleteAwardDialogOpen(false);
    setAwardToDelete(null);
  };

  const cancelDeleteAward = () => {
    setDeleteAwardDialogOpen(false);
    setAwardToDelete(null);
  };

  const handleEditNews = (news) => {
    setCurrentNews(news);
    setShowNewsModal(true);
  };

  const handleCreateNews = () => {
    setCurrentNews(null);
    setShowNewsModal(true);
  };

  // Modal submit handlers
  const handleSeriesModalSubmit = async (e, formData) => {
    e.preventDefault();
    try {
      if (currentSeries) {
        await handleUpdate('series', currentSeries.id, formData);
      } else {
        await handleCreate('series', formData);
      }
      setShowSeriesModal(false);
      setCurrentSeries(null);
    } catch (err) {
      console.error('Error submitting series form:', err);
      throw err;
    }
  };

  const handleColorModalSubmit = async (e, formData) => {
    e.preventDefault();
    console.log('ColorModalSubmit called with formData:', formData);
    try {
      if (currentColor) {
        console.log('Updating color:', currentColor.id, formData);
        await handleUpdate('colors', currentColor.id, formData);
      } else {
        console.log('Creating new color:', formData);
        await handleCreate('colors', formData);
      }
      console.log('Color operation successful!');
      setShowColorModal(false);
      setCurrentColor(null);
    } catch (err) {
      console.error('Error submitting color form:', err);
      throw err; // Re-throw damit Modal den Fehler bekommt
    }
  };

  const handleAwardModalSubmit = async (e, formData) => {
    e.preventDefault();
    try {
      if (currentAward) {
        await handleUpdate('awards', currentAward.id, formData);
      } else {
        await handleCreate('awards', formData);
      }
      setShowAwardModal(false);
      setCurrentAward(null);
    } catch (err) {
      console.error('Error submitting award form:', err);
      throw err;
    }
  };

  const handleExhibitionModalSubmit = async (e, formData) => {
    e.preventDefault();
    try {
      if (currentExhibition) {
        await handleUpdate('exhibitions', currentExhibition.id, formData);
      } else {
        await handleCreate('exhibitions', formData);
      }
      setShowExhibitionModal(false);
      setCurrentExhibition(null);
    } catch (err) {
      console.error('Error submitting exhibition form:', err);
      throw err;
    }
  };


  if (loading) {
  return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    window.location.href = '/login';
    return null;
  }

  return (
    <div className="min-h-screen w-screen bg-gray-100 dark:bg-zinc-900 transition-colors duration-300">
      <div className="w-full px-2 sm:px-4 md:px-12 lg:px-24 py-4 md:py-8">
        {/* Tab Bar */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-6 rounded-2xl bg-white/95 dark:bg-zinc-800/90 shadow-lg p-1 md:p-4 sticky top-0 z-20 border border-gray-200 dark:border-zinc-700 backdrop-blur-sm">
          {[
            { icon: HomeIcon, label: 'Home', title: 'Home' },
            { icon: PhotoIcon, label: `Gallery${paintings?.length ? ` (${paintings.length})` : ' (0)'}`, title: 'Gallery' },
            { icon: Squares2X2Icon, label: `Series${series?.length ? ` (${series.length})` : ' (0)'}`, title: 'Series' },
            { icon: SwatchIcon, label: `Colors${colors?.length ? ` (${colors.length})` : ' (0)'}`, title: 'Colors' },
            { icon: BuildingLibraryIcon, label: `Exhibitions${exhibitions?.length ? ` (${exhibitions.length})` : ' (0)'}`, title: 'Exhibitions' },
            { icon: TrophyIcon, label: `Awards${awards?.length ? ` (${awards.length})` : ' (0)'}`, title: 'Awards' },
            { icon: PhotoIcon, label: `News${news?.length ? ` (${news.length})` : ' (0)'}`, title: 'News' },
            { icon: HiOutlineChartBar, label: 'Statistiken', title: 'Statistiken' },
          ].map((tab, idx) => {
            const Icon = tab.icon;
            const isActive = activeTab === idx;
            return (
              <button
                key={tab.label}
                onClick={() => setActiveTab(idx)}
                className={`group relative flex flex-col items-center justify-center px-3 py-2 md:px-6 md:py-3 rounded-xl transition-all duration-300 text-[12px] md:text-base font-medium gap-1 md:gap-2
                  ${isActive
                    ? 'bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 text-white shadow-lg scale-105'
                    : 'bg-gray-100 dark:bg-zinc-900 text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-zinc-800'
                  }`}
                title={tab.title}
                style={{ touchAction: 'manipulation' }}
              >
                <div className={`absolute inset-0 rounded-xl transition-opacity duration-300 ${
                  isActive ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'
                } bg-gradient-to-br from-blue-500/10 to-blue-600/10 dark:from-blue-400/10 dark:to-blue-500/10`} />
                <Icon className={`w-5 h-5 md:w-6 md:h-6 transition-transform duration-300 ${
                  isActive ? 'scale-110' : 'group-hover:scale-110'
                }`} />
                <span className={`transition-transform duration-300 ${
                  isActive ? 'scale-105' : 'group-hover:scale-105'
                }`}>
                  {tab.label}
                </span>
                {isActive && (
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1/2 h-0.5 bg-white dark:bg-blue-400 rounded-full" />
                )}
              </button>
            );
          })}
        </div>
        {/* Forms */}
        <div className="w-full max-w-5xl mx-auto p-2 md:p-6">
          <TabPanel value={activeTab} index={0}>
            <HomeForm 
              formData={homeFormData} 
              handleChange={handleHomeChange} 
              handleImageChange={handleHomeImageChange} 
              handleSubmit={handleHomeSubmit} 
            />
            <h3 className="mt-12 mb-4 text-xl font-semibold text-gray-800 dark:text-gray-100">Live-Vorschau</h3>
            <HomePreview formData={homeFormData} />
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <GalleryAdmin paintingViews={paintingViews} />
          </TabPanel>
          <TabPanel value={activeTab} index={2}>
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Serien verwalten</h3>
                <button
                  onClick={handleCreateSeries}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
                >
                  Neue Serie anlegen
                </button>
              </div>
              <SeriesList
                series={Array.isArray(series) ? series : []}
                paintings={Array.isArray(paintings) ? paintings : []}
                onEdit={handleEditSeries}
                onDelete={handleDeleteSeries}
                entityViews={seriesViews}
              />
            </div>
            <SeriesFormModal
              isOpen={showSeriesModal}
              onClose={() => {
                setShowSeriesModal(false);
                setCurrentSeries(null);
              }}
              series={currentSeries}
              onSubmit={handleSeriesModalSubmit}
            />
            <AlertDialog
              open={deleteDialogOpen}
              title="Serie löschen?"
              description="Diese Aktion kann nicht rückgängig gemacht werden. Die Serie wird dauerhaft gelöscht."
              confirmText="Löschen"
              cancelText="Abbrechen"
              onConfirm={confirmDeleteSeries}
              onCancel={cancelDeleteSeries}
              confirmColor="red"
            />
          </TabPanel>
          <TabPanel value={activeTab} index={3}>
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Farben verwalten</h3>
                <button
                  onClick={handleCreateColor}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
                >
                  Neue Farbe anlegen
                </button>
              </div>
              <ColorList
                colors={Array.isArray(colors) ? colors : []}
                paintings={Array.isArray(paintings) ? paintings : []}
                onEdit={handleEditColor}
                onDelete={handleDeleteColor}
                entityViews={colorViews}
              />
            </div>
            <ColorFormModal
              isOpen={showColorModal}
              onClose={() => {
                setShowColorModal(false);
                setCurrentColor(null);
              }}
              color={currentColor}
              onSubmit={handleColorModalSubmit}
            />
            <AlertDialog
              open={deleteColorDialogOpen}
              title="Farbe löschen?"
              description="Diese Aktion kann nicht rückgängig gemacht werden. Die Farbe wird dauerhaft gelöscht."
              confirmText="Löschen"
              cancelText="Abbrechen"
              onConfirm={confirmDeleteColor}
              onCancel={cancelDeleteColor}
              confirmColor="red"
            />
          </TabPanel>
          <TabPanel value={activeTab} index={4}>
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Ausstellungen verwalten</h3>
                <button
                  onClick={handleCreateExhibition}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
                >
                  Neue Ausstellung anlegen
                </button>
              </div>
              <ExhibitionList
                exhibitions={Array.isArray(exhibitions) ? exhibitions : []}
                paintings={Array.isArray(paintings) ? paintings : []}
                onEdit={handleEditExhibition}
                onDelete={handleDeleteExhibition}
                entityViews={exhibitionViews}
              />
            </div>
            <ExhibitionFormModal
              isOpen={showExhibitionModal}
              onClose={() => {
                setShowExhibitionModal(false);
                setCurrentExhibition(null);
              }}
              exhibition={currentExhibition}
              onSubmit={handleExhibitionModalSubmit}
            />
            <AlertDialog
              open={deleteExhibitionDialogOpen}
              title="Ausstellung löschen?"
              description="Diese Aktion kann nicht rückgängig gemacht werden. Die Ausstellung wird dauerhaft gelöscht."
              confirmText="Löschen"
              cancelText="Abbrechen"
              onConfirm={confirmDeleteExhibition}
              onCancel={cancelDeleteExhibition}
              confirmColor="red"
            />
          </TabPanel>
          <TabPanel value={activeTab} index={5}>
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Awards verwalten</h3>
                <button
                  onClick={handleCreateAward}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
                >
                  Neuen Award anlegen
                </button>
              </div>
              <AwardList
                awards={Array.isArray(awards) ? awards : []}
                paintings={Array.isArray(paintings) ? paintings : []}
                onEdit={handleEditAward}
                onDelete={handleDeleteAward}
                entityViews={awardViews}
              />
            </div>
            <AwardFormModal
              isOpen={showAwardModal}
              onClose={() => {
                setShowAwardModal(false);
                setCurrentAward(null);
              }}
              award={currentAward}
              onSubmit={handleAwardModalSubmit}
            />
            <AlertDialog
              open={deleteAwardDialogOpen}
              title="Award löschen?"
              description="Diese Aktion kann nicht rückgängig gemacht werden. Der Award wird dauerhaft gelöscht."
              confirmText="Löschen"
              cancelText="Abbrechen"
              onConfirm={confirmDeleteAward}
              onCancel={cancelDeleteAward}
              confirmColor="red"
            />
          </TabPanel>
          <TabPanel value={activeTab} index={6}>
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">News verwalten</h3>
                <button
                  onClick={handleCreateNews}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
                >
                  News erstellen
                </button>
              </div>
              <NewsList
                onEdit={handleEditNews}
                onDelete={(id) => handleDelete('news', id)}
              />
            </div>
            <NewsFormModal
              isOpen={showNewsModal}
              onClose={() => {
                setShowNewsModal(false);
                setCurrentNews(null);
              }}
              news={currentNews}
            />
          </TabPanel>
          <TabPanel value={activeTab} index={7}>
            <AdminStats />
          </TabPanel>
                </div>
              </div>
            </div>
    );
  }

function GalleryAdmin({ paintingViews }) {
  const { paintings, series, colors, awards, exhibitions, handleCreate, handleUpdate, handleDelete, loading, fetchData } = useAdmin();
  const [editId, setEditId] = useState(null);
  const [showPaintingModal, setShowPaintingModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description_de: '',
    description_en: '',
    year: '',
    size: '',
    price: '',
    serie_id: '',
    color_ids: [],
    award_ids: [],
    exhibition_ids: [],
    images: [],
    main_image_idx: 0,
    sold: false
  });

  // Wenn editId gesetzt, Painting suchen und FormData setzen
  React.useEffect(() => {
    const safePaintings = Array.isArray(paintings) ? paintings : [];
    if (editId) {
      const painting = safePaintings.find(p => p.id === editId);
      if (painting) {
        setFormData(prev => ({ ...prev, ...painting, id: painting.id }));
      }
    } else {
      setFormData({
        name: '',
        description_de: '',
        description_en: '',
        year: '',
        size: '',
        price: '',
        serie_id: '',
        color_ids: [],
        award_ids: [],
        exhibition_ids: [],
        images: [],
        main_image_idx: 0,
        sold: false
      });
    }
  }, [editId, paintings]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e, field) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setFormData(prev => ({ ...prev, [field]: file }));
  };

  const handleMainImageIdx = (idx) => {
    setFormData(prev => ({ ...prev, main_image_idx: idx }));
  };

  const handleSubmit = async (e, modalFormData) => {
    e.preventDefault();
    try {
      // Use modalFormData if provided (from modal), otherwise use formData
      const dataToSubmit = modalFormData || formData;
      
      if (editId) {
        await handleUpdate('paintings', editId, dataToSubmit);
        // Reset edit mode first
        setEditId(null);
        setShowPaintingModal(false);
        // Then refresh the paintings list
        await fetchData('paintings');
      } else {
        await handleCreate('paintings', dataToSubmit);
        setShowPaintingModal(false);
        // Refresh paintings list after create
        await fetchData('paintings');
      }
      // Reset form data after successful submission
      setFormData({
        name: '',
        description_de: '',
        description_en: '',
        year: '',
        size: '',
        price: '',
        serie_id: '',
        color_ids: [],
        award_ids: [],
        exhibition_ids: [],
        images: [],
        main_image_idx: 0,
        sold: false
      });
    } catch (error) {
      console.error('Error submitting painting:', error);
    }
  };

  const handleEdit = (id) => {
    setEditId(id);
    setShowPaintingModal(true);
  };
  const handleDeletePainting = async (id) => {
    await handleDelete('paintings', id);
    if (editId === id) setEditId(null);
    // Refresh the list after deletion
    await fetchData('paintings');
  };
  const handleCancel = () => {
    setEditId(null);
    setFormData({
      name: '',
      description_de: '',
      description_en: '',
      year: '',
      size: '',
      price: '',
      serie_id: '',
      color_ids: [],
      award_ids: [],
      exhibition_ids: [],
      images: [],
      main_image_idx: 0,
      sold: false
    });
  };

  const handleSeriesUpdate = async (updatedSeries) => {
    // Update the series list in the AdminContext
    await fetchData('series');
  };

  const handleAwardsUpdate = async (updatedAward) => {
    // Update the awards list in the AdminContext
    await fetchData('awards');
  };

  if (loading) {
    return <div className="text-center text-gray-500 dark:text-gray-400 py-8">Lade Daten...</div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Gemälde verwalten</h3>
        <button
          onClick={() => {
            setEditId(null);
            setFormData({
              name: '',
              description_de: '',
              description_en: '',
              year: '',
              size: '',
              price: '',
              serie_id: '',
              color_ids: [],
              award_ids: [],
              exhibition_ids: [],
              images: [],
              main_image_idx: 0,
              sold: false,
              featured: false
            });
            setShowPaintingModal(true);
          }}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
        >
          Neues Gemälde anlegen
        </button>
      </div>
      
      <PaintingList
        paintings={paintings}
        series={Array.isArray(series) ? series : []}
        colors={Array.isArray(colors) ? colors : []}
        awards={Array.isArray(awards) ? awards : []}
        exhibitions={Array.isArray(exhibitions) ? exhibitions : []}
        paintingViews={paintingViews}
        onEdit={handleEdit}
        onDelete={handleDeletePainting}
      />

      <PaintingFormModal
        isOpen={showPaintingModal}
        onClose={() => {
          setShowPaintingModal(false);
          setEditId(null);
        }}
        painting={editId ? formData : null}
        onSubmit={handleSubmit}
        series={Array.isArray(series) ? series : []}
        colors={Array.isArray(colors) ? colors : []}
        awards={Array.isArray(awards) ? awards : []}
        exhibitions={Array.isArray(exhibitions) ? exhibitions : []}
        onSeriesUpdate={handleSeriesUpdate}
        onAwardsUpdate={handleAwardsUpdate}
      />
    </div>
  );
}

export function AdminPage() {
  useAuthGuard();
                return (
    <AdminProvider>
      <AdminContent />
    </AdminProvider>
  );
} 